import React, { FunctionComponent, useState } from 'react';
import ShareSendClients from './ShareSendClients/ShareSendClients';
import { ThreeDotsDropdown } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { ThreeDotsDropdownOption } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdownOption';
import DeactivateModal from 'components/UI/V3/Modals/DeleteModal';
import { useDispatch } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import { updateFormThunk, deleteFormThunk } from '@spike/forms-action';
import { showSuccessThunk, showErrorThunk } from '@spike/notifications-action';
import { SaveForm } from '@spike/form-model';
import { Form } from 'model/Forms';
import {
    faPaperPlane,
    faFileDownload,
    faPrint,
    faPen,
    faCopy,
    faBan,
    faCheck,
    faTrash
} from '@fortawesome/pro-light-svg-icons';

interface FormsOptionsProps {
    form: Form;
    onDeleted?: () => void;
    onEdit?: (form: Form) => void;
    // Called after a form is updated (e.g. deactivated) so parent can refresh current list/filters
    onUpdated?: () => void;
    onOpenShareSendClients?: (formId?: number) => void;
}

export enum OptionTypes {
    Send = 'send',
    Download = 'download',
    Print = 'print',
    Edit = 'edit',
    Duplicate = 'duplicate',
    Activate = 'activate',
    Deactivate = 'deactivate',
    Delete = 'delete'
}

const FormsOptions: FunctionComponent<FormsOptionsProps> = props => {
    const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);

    const options: ThreeDotsDropdownOption[] = [
        {
            id: OptionTypes.Send,
            label: 'Send & Share',
            icon: faPaperPlane,
            onClick: () => setSelectedOptionId(OptionTypes.Send)
        },
        {
            id: OptionTypes.Download,
            label: 'Download PDF',
            icon: faFileDownload,
            onClick: () => setSelectedOptionId(OptionTypes.Download)
        },
        {
            id: OptionTypes.Print,
            label: 'Print',
            icon: faPrint,
            onClick: () => setSelectedOptionId(OptionTypes.Print)
        },
        {
            id: OptionTypes.Edit,
            label: 'Edit',
            icon: faPen,
            onClick: () => setSelectedOptionId(OptionTypes.Edit)
        },
        {
            id: OptionTypes.Duplicate,
            label: 'Duplicate',
            icon: faCopy,
            onClick: () => setSelectedOptionId(OptionTypes.Duplicate)
        },
        {
            id: OptionTypes.Deactivate,
            label: 'Deactivate',
            icon: faBan,
            onClick: () => setSelectedOptionId(OptionTypes.Deactivate)
        },
        {
            id: OptionTypes.Activate,
            label: 'Activate',
            icon: faCheck,
            onClick: () => setSelectedOptionId(OptionTypes.Activate)
        },
        {
            id: OptionTypes.Delete,
            label: 'Delete',
            icon: faTrash,
            onClick: () => setSelectedOptionId(OptionTypes.Delete)
        }
    ];

    // MVP: Only show Share & Send, Edit, and either Deactivate or Activate depending on form.active.
    const filteredOptions = options.filter(option => {
        if (option.id === OptionTypes.Edit) {
            return true;
        }

        if (props.form.active && option.id === OptionTypes.Send) return true;
        // Show Deactivate when form is active; show Activate when form is inactive
        if (props.form.active && option.id === OptionTypes.Deactivate) return true;
        if (!props.form.active && option.id === OptionTypes.Activate) return true;

        return false;
    });

    const exportResponses = () => {
        if (!props.form.responses || props.form.responses.length === 0) return;

        // Create CSV content
        const headers = ['Response ID', 'Client ID', 'Pet ID', 'Submitted At', 'Status'];
        const fieldHeaders = props.form.fields.map(field => field.label);
        const allHeaders = [...headers, ...fieldHeaders];

        const csvContent = [
            allHeaders.join(','),
            ...props.form.responses.map(response => {
                const baseData = [
                    response.id,
                    response.clientId || '',
                    response.petId || '',
                    response.submittedAt.toISOString(),
                    response.status
                ];
                const fieldData = props.form.fields.map(field => {
                    const value = response.responses[field.id] || '';
                    return `"${value}"`;
                });
                return [...baseData, ...fieldData].join(',');
            })
        ].join('\n');

        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${props.form.title}_responses.csv`);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
    };

    // Deactivate modal state
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [deactivateLoading, setDeactivateLoading] = useState(false);
    const [showActivateModal, setShowActivateModal] = useState(false);
    const [activateLoading, setActivateLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [showShareSendClients, setShowShareSendClients] = useState(false);
    React.useEffect(() => {
        if (selectedOptionId === OptionTypes.Download) {
            exportResponses();
            setSelectedOptionId(null);
        } else if (selectedOptionId === OptionTypes.Delete) {
            setShowDeleteModal(true);
            setSelectedOptionId(null);
        } else if (selectedOptionId === OptionTypes.Send) {
            // Ask parent to open the ShareSendClients panel at a stable top-level location
            // pass the current form id so the panel can preselect it
            props.onOpenShareSendClients && props.onOpenShareSendClients(props.form.id);
            setSelectedOptionId(null);
        } else if (selectedOptionId === OptionTypes.Print) {
            dispatch(showErrorThunk('Print functionality will be implemented'));
            setSelectedOptionId(null);
        } else if (selectedOptionId === OptionTypes.Edit) {
            props.onEdit && props.onEdit(props.form);
            setSelectedOptionId(null);
        } else if (selectedOptionId === OptionTypes.Duplicate) {
            dispatch(showErrorThunk('Duplicate functionality will be implemented'));
            setSelectedOptionId(null);
        } else if (selectedOptionId === OptionTypes.Deactivate) {
            setShowDeactivateModal(true);
            setSelectedOptionId(null);
        } else if (selectedOptionId === OptionTypes.Activate) {
            setShowActivateModal(true);
            setSelectedOptionId(null);
        }
    }, [selectedOptionId]);

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper() as any;

    const handleDeactivate = async () => {
        setDeactivateLoading(true);
        try {
            const partialPayload = { active: false } as Partial<SaveForm>;
            const result: any = await dispatch(
                (updateFormThunk as any)(apiClientWrapper, String(props.form.id), partialPayload) as any
            );

            // Normalize thunk result: payload may be in result.payload or result itself
            const payload = result && (result.payload ?? result);
            const hadError = !!(result && (result.error || (payload && (payload as any).error)));

            if (hadError || !payload) {
                const errMsg =
                    (result && (result.error?.message || (payload && (payload as any).message))) ||
                    'Unable to deactivate form. Please try again.';
                dispatch(showErrorThunk(errMsg));
                setDeactivateLoading(false);
                setShowDeactivateModal(false);
                return;
            }

            // Success: notify and ask parent to refresh
            dispatch(showSuccessThunk('Form deactivated successfully'));
            setShowDeactivateModal(false);
            setDeactivateLoading(false);

            if (props.onUpdated) {
                // allow parent to re-fetch using its current filters/pagination
                await props.onUpdated();
            }
        } catch (err: any) {
            const message = err?.message || 'Unable to deactivate form. Please try again.';
            dispatch(showErrorThunk(message));
            setDeactivateLoading(false);
            setShowDeactivateModal(false);
        }
    };

    const handleActivate = async () => {
        setActivateLoading(true);
        try {
            const partialPayload = { active: true } as Partial<SaveForm>;
            const result: any = await dispatch(
                (updateFormThunk as any)(apiClientWrapper, String(props.form.id), partialPayload) as any
            );

            const payload = result && (result.payload ?? result);
            const hadError = !!(result && (result.error || (payload && (payload as any).error)));

            if (hadError || !payload) {
                const errMsg =
                    (result && (result.error?.message || (payload && (payload as any).message))) ||
                    'Unable to activate form. Please try again.';
                dispatch(showErrorThunk(errMsg));
                setActivateLoading(false);
                setShowActivateModal(false);
                return;
            }

            dispatch(showSuccessThunk('Form activated successfully'));
            setShowActivateModal(false);
            setActivateLoading(false);

            if (props.onUpdated) {
                await props.onUpdated();
            }
        } catch (err: any) {
            const message = err?.message || 'Unable to activate form. Please try again.';
            dispatch(showErrorThunk(message));
            setActivateLoading(false);
            setShowActivateModal(false);
        }
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            const result: any = await dispatch(
                (deleteFormThunk as any)(apiClientWrapper, String(props.form.id)) as any
            );
            const payload = result && (result.payload ?? result);
            const hadError = !!(result && (result.error || (payload && (payload as any).error)));

            if (hadError) {
                const errMsg =
                    (result && (result.error?.message || (payload && (payload as any).message))) ||
                    'Unable to delete form. Please try again.';
                dispatch(showErrorThunk(errMsg));
                setDeleteLoading(false);
                setShowDeleteModal(false);
                return;
            }

            dispatch(showSuccessThunk('Form deleted successfully'));
            setDeleteLoading(false);
            setShowDeleteModal(false);

            if (props.onUpdated) {
                await props.onUpdated();
            }
        } catch (err: any) {
            const message = err?.message || 'Unable to delete form. Please try again.';
            dispatch(showErrorThunk(message));
            setDeleteLoading(false);
            setShowDeleteModal(false);
        }
    };

    return (
        <>
            <ThreeDotsDropdown
                options={filteredOptions}
                direction="down"
            />
            {showDeactivateModal && (
                <DeactivateModal
                    title="Deactivate Form"
                    question={'Are you sure you want to deactivate'}
                    entityName={props.form.title}
                    isLoading={deactivateLoading}
                    onConfirm={handleDeactivate}
                    onCancel={() => setShowDeactivateModal(false)}
                />
            )}
            {showDeleteModal && (
                <DeactivateModal
                    title="Delete Form"
                    entityName={props.form.title}
                    isLoading={deleteLoading}
                    onConfirm={handleDelete}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}
            {showActivateModal && (
                <DeactivateModal
                    title="Activate Form"
                    question={'Are you sure you want to activate'}
                    entityName={props.form.title}
                    isLoading={activateLoading}
                    onConfirm={handleActivate}
                    onCancel={() => setShowActivateModal(false)}
                />
            )}
            {/* success toast is shown globally via notifications middleware */}
            {/* ShareSendClients is rendered at a higher level (Forms) to avoid being unmounted
                when the table re-renders. Parent should provide onOpenShareSendClients to open it. */}
        </>
    );
};

export default FormsOptions;
